import { InputOption, JsonValue } from 'models';
import { AppModule, DeviceRole, ProductionDeviceType } from 'utils/constants';
import { i18nValue, parseToNumberWithDot } from './index';
import { FormValidationType } from 'models/form';

import {
  CharacteristicSpecificationInterface,
  CharacteristicValueInterface,
  CharacteristicValueSpecificationInterface,
  ChargingCharacteristicData,
  CriticalCharacteristicsConfig,
  DischargeCharacteristicData
} from 'models/characteristics';

// ==============================|| COMMON UTILS ||============================== //

export enum ReservedCapacityGuardMode {
  AUTO = 'auto',
  MANUAL = 'manual'
}

export enum DeviceInstallationStatus {
  INSTALLED = 'INSTALLED',
  NOT_INSTALLED = 'NOT_INSTALLED'
}

export enum PoweroutputChangeType {
  ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH = 'ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH',
  AVAILABLE_CONSUMPTION_POWER_STAGES_SWITCH = 'AVAILABLE_CONSUMPTION_POWER_STAGES_SWITCH',
  LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_FIXED = 'LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_FIXED',
  LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_ADJUSTABLE = 'LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_ADJUSTABLE',
  FIXED_CONSUMPTION_PROFILE = 'FIXED_CONSUMPTION_PROFILE',
  NONLINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE = 'NONLINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE'
}

export enum UnitType {
  PCS = 'PCS',
  SQUARE_METRE = 'SQUARE_METRE',
  SQUARE_MILIMETRE = 'SQUARE_MILIMETRE',
  PERCENT = 'PERCENT',
  WATT_PEAK = 'WATT_PEAK',
  EUR = 'EUR',
  EUR_PER_KILOWATT_HOUR = 'EUR_PER_KILOWATT_HOUR',
  EUR_PER_MEGAWATT_HOUR = 'EUR_PER_MEGAWATT_HOUR',
  DEGREE_CELSIA = 'DEGREE_CELSIA',
  WATT_PER_SQUARE_METRE = 'WATT_PER_SQUARE_METRE',
  DEGREE = 'DEGREE',
  METRE = 'METRE',
  MILLIMETRE = 'MILLIMETRE',
  METRE_PER_SECOND = 'METRE_PER_SECOND',
  KWH = 'kWh',
  MWH = 'MWh',
  KW = 'kW',
  MW = 'MW',
  SECOND = 'SECOND',
  EUR_PER_HOUR = 'EUR_PER_HOUR',
  KILOWATT_PER_SECOND = 'KILOWATT_PER_SECOND',
  A = 'A',
  V = 'V',
  Hz = 'Hz',
  KVAR = 'kVAr',
  METERS_ABOVE_SEA_LEVEL = 'METERS_ABOVE_SEA_LEVEL',
  CUBIC_METRE_PER_SECOND = 'CUBIC_METRE_PER_SECOND',
  W_PER_SQUARE_METRE = 'W_PER_SQUARE_METRE'
}

const ConsumptionEntireAvailablePowerSwitchCriticalCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: ['pmax_consumption', 'pmin_consumption'],
    nonCriticalCharacteristics: ['consumption_change_unitcost']
  };

const ConsumptionLinearPowerChangeFixedCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: ['pmax_consumption', 'poweroutput_rate_of_change'],
    nonCriticalCharacteristics: [
      'pmin_consumption',
      'consumption_change_unitcost'
    ]
  };

const ConsumptionLinearPowerChangeAdjustableCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: [
      'pmax_consumption',
      'max_poweroutput_rate_of_change'
    ],
    nonCriticalCharacteristics: [
      'pmin_consumption',
      'consumption_change_unitcost',
      'min_poweroutput_rate_of_change'
    ]
  };

const ConsumptionFixedProfileCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: ['pmax_consumption', 'pmin_consumption'],
    nonCriticalCharacteristics: []
  };

const ProductionBatteryCriticalCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: [
      'usable_energy',
      'charging_characteristic',
      'discharge_characteristic'
    ],
    nonCriticalCharacteristics: [
      'min_soc_end_condition',
      'max_soc_end_condition',
      'min_soc',
      'max_soc',
      'bat_efficiency',
      'storage_discounted_levelized_cost'
    ]
  };
const ProductionFVECriticalCharacteristicsConfig: CriticalCharacteristicsConfig =
  {
    criticalCharacteristics: ['solcast_resource_id'],
    nonCriticalCharacteristics: ['electricity_levelized_cost']
  };

export const findCharacteristicSpecificationByName = (
  array: CharacteristicSpecificationInterface[] | undefined,
  name: string | undefined
): CharacteristicSpecificationInterface | undefined => {
  if (!array || !name) return undefined;

  return array.find((element) => {
    return element.name === name;
  });
};

export const findCharacteristicByName = (
  array: CharacteristicValueInterface[] | undefined,
  name: string | undefined
): CharacteristicValueInterface | undefined => {
  if (!array || !name) return undefined;

  return array.find((element) => {
    return element.name === name;
  });
};

export const getCharacteristicDefaultValue = (
  array: CharacteristicSpecificationInterface[] | undefined,
  name: string | undefined
): string | boolean | undefined => {
  const characteristic = findCharacteristicSpecificationByName(array, name);

  return (
    characteristic?.valueSpecification.find(
      (specification) => specification.isDefault
    )?.value ?? ''
  );
};

export const getSelectOptionsFromCharacteristicSpecifications = (
  specifications: CharacteristicSpecificationInterface[] | undefined,
  characteristicType: string | undefined
): InputOption[] | undefined => {
  const specification = findCharacteristicSpecificationByName(
    specifications,
    characteristicType
  );

  return specification?.valueSpecification.map((element) => {
    return {
      value: element.value,
      label: i18nValue(
        `module.${AppModule.EAI}.codebooks.${element.value}.title`,
        element.value
      )
    };
  });
};

export const getJsonValue = (
  characteristicValues?: CharacteristicValueInterface[],
  characteristicSpecification?: CharacteristicSpecificationInterface[],
  name?: string
): JsonValue | undefined => {
  if (!characteristicValues || !characteristicSpecification || !name)
    return undefined;

  const characteristicValue = findCharacteristicByName(
    characteristicValues,
    name
  );

  return characteristicValue?.jsonValue;
};

export const getStringValueOrDefaultValue = (
  characteristicValues: CharacteristicValueInterface[] | undefined,
  characteristicSpecification:
    | CharacteristicSpecificationInterface[]
    | undefined,
  name: string | undefined
): string | undefined => {
  if (!characteristicValues || !characteristicSpecification || !name) {
    return undefined;
  }

  const characteristicValue = findCharacteristicByName(
    characteristicValues,
    name
  );

  return (
    characteristicValue?.stringValue ??
    getCharacteristicDefaultValue(characteristicSpecification, name)?.toString()
  );
};

export const getNumberValueOrDefaultValue = (
  characteristicValues: CharacteristicValueInterface[] | undefined,
  characteristicSpecifications:
    | CharacteristicSpecificationInterface[]
    | undefined,
  name: string | undefined
): string | undefined => {
  if (!characteristicValues || !characteristicSpecifications || !name) {
    return undefined;
  }

  const characteristicValue = findCharacteristicByName(
    characteristicValues,
    name
  );

  return (
    characteristicValue?.numberValue ??
    getCharacteristicDefaultValue(
      characteristicSpecifications,
      name
    )?.toString()
  );
};

export const getBooleanValueOrDefaultValue = (
  characteristicValues: CharacteristicValueInterface[] | undefined,
  characteristicSpecification:
    | CharacteristicSpecificationInterface[]
    | undefined,
  name: string | undefined
): boolean | undefined => {
  if (!characteristicValues || !characteristicSpecification || !name)
    return undefined;

  const characteristicValue = findCharacteristicByName(
    characteristicValues,
    name
  );

  return (
    characteristicValue?.booleanValue ??
    !!getCharacteristicDefaultValue(characteristicSpecification, name)
  );
};

export const checkValueRange = (
  value: string,
  from?: string,
  to?: string
): boolean => {
  let valid = true;

  if (from) {
    valid = parseFloat(value) >= parseFloat(from);
  }

  if (to) {
    valid = valid && parseFloat(value) <= parseFloat(to);
  }

  return valid;
};

export const checkValueRangeFromSpecification = (
  value: string,
  valueCharacteristic: CharacteristicSpecificationInterface
): boolean => {
  if (value) {
    const valueSpecification: CharacteristicValueSpecificationInterface =
      valueCharacteristic?.valueSpecification[0];

    if (valueSpecification) {
      return checkValueRange(
        value,
        valueSpecification['valueFrom'],
        valueSpecification['valueTo']
      );
    }
  }
  return true;
};

export const getErrorMessage = (
  i18nRoot: string,
  array: CharacteristicSpecificationInterface[] | undefined,
  name: string | undefined,
  error: string
): string => {
  let errorMessage = i18nValue(`${i18nRoot}.${error}`, error);

  if (error !== 'value-number-range') {
    return errorMessage;
  }

  const characteristic = findCharacteristicSpecificationByName(array, name);
  const characteristicSpecification:
    | CharacteristicValueSpecificationInterface
    | undefined = characteristic?.valueSpecification[0];

  if (characteristicSpecification && characteristicSpecification['valueFrom']) {
    errorMessage += ' ' + characteristic?.valueSpecification[0]['valueFrom'];
  }

  if (characteristicSpecification && characteristicSpecification['valueTo']) {
    errorMessage += ' - ' + characteristic?.valueSpecification[0]['valueTo'];
  }

  return errorMessage;
};

export const checkChargingCharacteristic = (
  characteristic: JsonValue
): boolean => {
  if (!Array.isArray(characteristic) || !characteristic.length) return false;

  const chargingCharacteristics = [
    ...characteristic
  ] as unknown as ChargingCharacteristicData[];

  const sortedChargingCharacteristics = chargingCharacteristics.sort(
    (a, b) => a.batteryCapacity - b.batteryCapacity
  );

  return (
    sortedChargingCharacteristics.at(0)?.batteryCapacity === 0 &&
    sortedChargingCharacteristics.at(-1)?.batteryCapacity === 100
  );
};

export const checkDischargeCharacteristic = (
  characteristic: JsonValue
): boolean => {
  if (!Array.isArray(characteristic) || !characteristic.length) return false;

  const dischargeCharacteristics = [
    ...characteristic
  ] as unknown as DischargeCharacteristicData[];

  const sortedDischargeCharacteristics = dischargeCharacteristics.sort(
    (a, b) => a.batteryCapacity - b.batteryCapacity
  );

  return (
    sortedDischargeCharacteristics.at(0)?.batteryCapacity === 0 &&
    sortedDischargeCharacteristics.at(-1)?.batteryCapacity === 100
  );
};

export const checkIfValueIsNumber = (): FormValidationType => {
  return {
    methodType: 'test',
    args: [
      'check-value',
      'value-number',
      (value: string) => {
        return value ? !isNaN(parseToNumberWithDot(value)) : true;
      }
    ]
  };
};

export const getCriticalCharacteristicLabel = (
  name: string,
  deviceRole: DeviceRole,
  type: ProductionDeviceType | string | undefined
): string => {
  if (!name || !deviceRole || !type) {
    return '';
  }

  if (deviceRole === DeviceRole.CONSUMPTION) {
    switch (type) {
      case PoweroutputChangeType.ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH:
        return getCriticalCharacteristicLabelFromConfig(
          name,
          ConsumptionEntireAvailablePowerSwitchCriticalCharacteristicsConfig
        );
      case PoweroutputChangeType.LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_FIXED:
        return getCriticalCharacteristicLabelFromConfig(
          name,
          ConsumptionLinearPowerChangeFixedCharacteristicsConfig
        );
      case PoweroutputChangeType.LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_ADJUSTABLE:
        return getCriticalCharacteristicLabelFromConfig(
          name,
          ConsumptionLinearPowerChangeAdjustableCharacteristicsConfig
        );
      case PoweroutputChangeType.FIXED_CONSUMPTION_PROFILE:
        return getCriticalCharacteristicLabelFromConfig(
          name,
          ConsumptionFixedProfileCharacteristicsConfig
        );
      default:
        return '';
    }
  }

  switch (type) {
    case ProductionDeviceType.BESS:
      return getCriticalCharacteristicLabelFromConfig(
        name,
        ProductionBatteryCriticalCharacteristicsConfig
      );
    case ProductionDeviceType.FVE:
      return getCriticalCharacteristicLabelFromConfig(
        name,
        ProductionFVECriticalCharacteristicsConfig
      );
    default:
      return '';
  }
};

export const getCriticalCharacteristicLabelFromConfig = (
  name: string,
  config: CriticalCharacteristicsConfig
): string => {
  if (config.criticalCharacteristics.includes(name)) {
    return '*';
  }
  if (config.nonCriticalCharacteristics.includes(name)) {
    return '**';
  }
  return '';
};

export enum CharacteristicsFVE {
  panel_count = 'panel_count',
  max_power_output = 'max_power_output',
  electricity_levelized_cost = 'electricity_levelized_cost',
  external_weather_monitoring = 'external_weather_monitoring'
}

export enum CharacteristicsBESS {
  usable_energy = 'usable_energy',
  storage_discounted_levelized_cost = 'storage_discounted_levelized_cost'
}

export enum CharacteristicsConsumption {
  poweroutput_change_type = 'poweroutput_change_type', // ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH
  pmin_consumption = 'pmin_consumption',
  pmax_consumption = 'pmax_consumption'
}

export enum CharacteristicsBalanceGroup {
  imbalance_speculation = 'imbalance_speculation',
  optimized_period_number = 'optimized_period_number'
}

export enum CharacteristicsRk {
  reserved_capacity = 'reserved_capacity',
  reserved_capacity_guard_phase_one_reserve = 'reserved_capacity_guard_phase_one_reserve',
  reserved_capacity_guard_phase_two_reserve = 'reserved_capacity_guard_phase_two_reserve',
  reserved_capacity_guard_mode = 'reserved_capacity_guard_mode',
  reserved_capacity_guard_period_start_restriction_offset = 'reserved_capacity_guard_period_start_restriction_offset',
  reserved_capacity_guard_earliest_restriction_start_offset = 'reserved_capacity_guard_earliest_restriction_start_offset',
  reserved_capacity_guard_check_periodicity = 'reserved_capacity_guard_check_periodicity',
  reserved_capacity_guard_check_algorithm = 'reserved_capacity_guard_check_algorithm',
  maximum_reserved_capacity = 'maximum_reserved_capacity',
  reserved_capacity_supply = 'reserved_capacity_supply',
  maximum_reserved_capacity_supply = 'maximum_reserved_capacity_supply',
  reserved_capacity_guard_enabled = 'reserved_capacity_guard_enabled',
  reserved_capacity_guard_priority = 'reserved_capacity_guard_priority',
  reserved_capacity_max_restriction_duration = 'reserved_capacity_max_restriction_duration',
  reserved_capacity_max_reduction_limit = 'reserved_capacity_max_reduction_limit',
  reserved_capacity_max_reduction_limit_type = 'reserved_capacity_max_reduction_limit_type',
  reserved_capacity_max_change_of_rate_of_power_change_reduction = 'reserved_capacity_max_change_of_rate_of_power_change_reduction',
  reserved_capacity_utilization_alarm_utilization_threshold = 'reserved_capacity_utilization_alarm_utilization_threshold',
  reserved_capacity_utilization_alarm_time_threshold = 'reserved_capacity_utilization_alarm_time_threshold'
}

export const getCharacteristicNumberValueByName = (
  deliveryPointCharacteristics: CharacteristicValueInterface[],
  characteristicName:
    | CharacteristicsRk
    | CharacteristicsFVE
    | CharacteristicsBESS
) => {
  const characteristic = findCharacteristicByName(
    deliveryPointCharacteristics,
    characteristicName
  );

  return characteristic?.numberValue ?? '';
};
